import React, { useRef, useState, lazy, Suspense, useEffect, useCallback } from 'react';
import { GeneratedDataItem } from './types';
import uploadFileToS3 from '../../../utils/uploadFileToS3';
import { toast } from 'react-toastify';

// Helper function to format text with bullet points
const formatTextWithBullets = (text: string): React.ReactElement[] => {
  const lines = text.split('\n');
  const result: React.ReactElement[] = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    
    if (!trimmed) {
      result.push(<br key={`br-${i}`} />);
      return;
    }
    
    // Handle numbered lists (1., 2., etc.)
    if (line.match(/^\d+\. /)) {
      const content = line.replace(/^\d+\. /, "");
      result.push(
        <div key={`ol-${i}`} className="flex ml-5 my-1">
          <span className="mr-2 text-blue-600 font-bold">•</span>
          <span>{content}</span>
        </div>
      );
    } 
    // Handle bullet points (- )
    else if (line.startsWith("- ")) {
      result.push(
        <div key={`ul-${i}`} className="flex ml-5 my-1">
          <span className="mr-2 text-blue-600">•</span>
          <span>{line.substring(2)}</span>
        </div>
      );
    }
    // Handle bullet points with • symbol
    else if (line.startsWith("• ")) {
      result.push(
        <div key={`bullet-${i}`} className="flex ml-5 my-1">
          <span className="mr-2 text-blue-600">•</span>
          <span>{line.substring(2)}</span>
        </div>
      );
    }
    // Handle regular text
    else {
      result.push(
        <div key={`text-${i}`} className="my-1">
          {trimmed}
        </div>
      );
    }
  });

  return result;
};

// Lazy load image panels
const ImageGeneratorPanel = lazy(() => import('./Imagegeneratorpanel'));
const ImageSearchPanel = lazy(() => import('./imagesearchpanel'));

interface TextAndImageComponentProps {
  item: GeneratedDataItem;
  editMode: boolean;
  editData: any;
  handleInputChange: (id: string, field: string, value: any, componentType?: string) => void;
  saveChanges: (id: string) => void;
  cancelEdit: (id: string) => void;
  hideImage?: boolean;
}

const TextAndImageComponent: React.FC<TextAndImageComponentProps> = ({
  item,
  editMode,
  editData,
  handleInputChange,
  saveChanges,
  cancelEdit,
  hideImage = false,
}) => {
  const textandimage = item.textandimage || { title: '', text: '', image: '' };
  
  // Debug: Log when item data changes
  useEffect(() => {
    console.log("TextAndImageComponent item updated:", {
      id: item._id,
      textandimage: item.textandimage,
      editMode,
      editData,
      editDataTextandimage: editData?.textandimage,
      itemDataText: (editData?.textandimage || textandimage).text,
      textareaValue: (editData?.textandimage || textandimage).text || ''
    });
  }, [item, editMode, editData, textandimage]);
  const id = item._id || '';
  const itemData = editData?.textandimage || textandimage;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [activePanel, setActivePanel] = useState<'image-generate' | 'image-search' | null>(null);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Debounced save function
  const debouncedSave = useCallback((fieldPath: string, value: any) => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    const timeout = setTimeout(async () => {
      try {
        console.log('Auto-saving changes for:', fieldPath, value?.length || value);
        handleInputChange(id, fieldPath, value, 'textandimage');
        // Don't call saveChanges here as it might interfere with edit mode
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 500); // 500ms delay
    
    setSaveTimeout(timeout);
  }, [id, handleInputChange, saveTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);

  if (editMode) {
    return (
      <div className="text-and-image-edit-component">
        {/* Title Input with Edit Button */}
        <div className="flex items-center mb-4">
          <div className="w-full relative">
            <input
              type="text"
              value={itemData.title || ''}
              onChange={(e) => {
                console.log('Title change:', e.target.value);
                debouncedSave('textandimage.title', e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded pr-8"
              placeholder="Title"
            />
            <span className="absolute right-2 top-2 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z"/>
              </svg>
            </span>
          </div>
        </div>

        {/* Two column layout - Text and Image (image can be hidden via hideImage prop) */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Left Column - Text */}
          <div className={`w-full ${hideImage ? "md:w-full" : "md:w-1/2"} border border-gray-300 rounded p-2`}>
            <h4 className="text-center border-b border-gray-300 pb-2 mb-2">Text</h4>
            <textarea
              value={itemData.text || ''}
              onChange={(e) => {
                console.log('Text change:', e.target.value.length, 'chars');
                debouncedSave('textandimage.text', e.target.value);
                const textarea = e.target;
                textarea.style.height = "auto";
                const maxHeight = 300;
                textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
              }}
              className="w-full p-2 border border-gray-300 rounded min-h-[300px] h-[40vh] max-h-[40vh] overflow-y-auto resize-none"
              placeholder="Enter text content here"
            />
          </div>

          {/* Right Column - Image (conditionally rendered) */}
          {!hideImage && (
            <div className="w-full md:w-1/2 border border-gray-300 rounded p-2">
              {activePanel === null && (
                <>
                  <h4 className="text-center border-b border-gray-300 pb-2 mb-2">Image</h4>
                  <input
                    type="text"
                    value={itemData.image || ''}
                    onChange={(e) => {
                      console.log('Image URL change:', e.target.value);
                      handleInputChange(id, 'textandimage.image', e.target.value, 'textandimage');
                    }}
                    className="w-full p-2 border border-gray-300 rounded mb-3"
                    placeholder="Image URL"
                  />
                  {itemData.image && (
                    <div className="mt-2 text-center">
                      <img
                        src={itemData.image}
                        alt="Preview"
                        className="max-w-full max-h-[200px] mx-auto object-contain border border-gray-200 rounded"
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap justify-center gap-2 mt-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        try {
                          setUploading(true);
                          const url = await uploadFileToS3({ name: f.name, type: f.type, body: f });
                          // update the item's image field
                          handleInputChange(id, 'textandimage.image', url, 'textandimage');
                          toast.success('Image uploaded');
                        } catch (err) {
                          console.error('Upload failed', err);
                          toast.error('Upload failed');
                        } finally {
                          setUploading(false);
                          // clear the input so same file can be selected again
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }
                      }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="px-3 py-1 rounded text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300"
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                    <button 
                      onClick={() => setActivePanel('image-search')}
                      className="px-3 py-1 rounded text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300"
                    >
                      Search
                    </button>
                    <button 
                      onClick={() => setActivePanel('image-generate')}
                      className="px-3 py-1 rounded text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300"
                    >
                      Refine
                    </button>
                  </div>
                </>
              )}

              {activePanel === 'image-generate' && (
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="font-medium">Image Generator</h4>
                    <button 
                      onClick={() => setActivePanel(null)}
                      className="px-2 py-1 text-sm rounded text-gray-600 hover:bg-gray-100"
                    >
                      Back
                    </button>
                  </div>
                  <Suspense fallback={<div className="text-center p-4">Loading image generator...</div>}>
                    <ImageGeneratorPanel 
                      onBack={() => setActivePanel(null)}
                      onSelectImage={async (imageUrl) => {
                        console.log('Generated image selected:', imageUrl);
                        handleInputChange(id, 'textandimage.image', imageUrl, 'textandimage');
                        setActivePanel(null);
                        
                        try {
                          await saveChanges(id);
                          console.log('Changes saved successfully');
                        } catch (error) {
                          console.error('Save failed:', error);
                          toast.error('Failed to save changes');
                        }
                      }}
                    />
                  </Suspense>
                </div>
              )}

              {activePanel === 'image-search' && (
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="font-medium">Image Search</h4>
                    <button 
                      onClick={() => setActivePanel(null)}
                      className="px-2 py-1 text-sm rounded text-gray-600 hover:bg-gray-100"
                    >
                      Back
                    </button>
                  </div>
                  <Suspense fallback={<div className="text-center p-4">Loading image search...</div>}>
                    <ImageSearchPanel 
                      onBack={() => setActivePanel(null)}
                      onSelectImage={async (imageUrl) => {
                        console.log('Search image selected:', imageUrl);
                        handleInputChange(id, 'textandimage.image', imageUrl, 'textandimage');
                        setActivePanel(null);
                        
                        try {
                          await saveChanges(id);
                          console.log('Changes saved successfully');
                        } catch (error) {
                          console.error('Save failed:', error);
                          toast.error('Failed to save changes');
                        }
                      }}
                    />
                  </Suspense>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Save/Cancel buttons */}
        {/* <div className="flex flex-col sm:flex-row justify-center gap-2 mt-4">
          <button

            className="px-4 py-2 rounded text-white bg-green-600 hover:bg-green-700 min-w-[100px]"
            onClick={() => {
              // Show feedback immediately
              toast.success("Changes saved successfully!");
              
              // Run all methods to try to force edit mode to close
              window.parent.postMessage({ type: "forceViewMode", componentId: id }, "*");
              document.dispatchEvent(new CustomEvent("closeEditMode", { detail: { componentId: id } }));
              
              // Then save changes
              saveChanges(id);
            }}
           
          >
            SAVE
          </button>
          <button
            onClick={() => cancelEdit(id)}
            className="px-4 py-2 rounded text-white bg-gray-600 hover:bg-gray-700 min-w-[100px]"
          >
            CANCEL
          </button>
        </div> */}
      </div>
    );
  }

  // View mode
  return (
    <div className="text-and-image-component flex flex-col md:flex-row">
      <div className={`w-full ${hideImage ? "md:w-full" : "md:w-1/2"} p-2`}>
        <h4 className="text-lg font-bold mb-2">{textandimage.title}</h4>
        <div className="text-sm whitespace-pre-line">
          {formatTextWithBullets(textandimage.text || '')}
        </div>
      </div>
      {/* Right column (image) is hidden when hideImage is true */}
      {!hideImage && (
        <div className="w-full md:w-1/2 p-2">
          {textandimage.image && (
            <img
              src={textandimage.image}
              alt={textandimage.title || 'Image'}
             className="rounded-md w-full max-h-[300px] object-contain"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TextAndImageComponent;
